import React, { useState, useEffect } from 'react';
import axios from 'axios';
import BASE_URL from '../../url';
import { X, Building2, Globe } from 'lucide-react';
import '../styles/PodModals.css'; 

const CreatePodModal = ({ isOpen, onClose, onPodCreated, currentRegion }) => {
  const [isICPod, setIsICPod] = useState(false);
  const [isBenchPod, setIsBenchPod] = useState(false);
  const [email, setEmail] = useState('');
  const [regionId, setRegionId] = useState('1'); // 1 = Onsite, 2 = Offshore
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const colors = [
    'linear-gradient(135deg, #3b82f6, #2563eb)', // Blue
    'linear-gradient(135deg, #8b5cf6, #6366f1)', // Purple
    'linear-gradient(135deg, #10b981, #059669)', // Green
    'linear-gradient(135deg, #f97316, #ea580c)', // Orange
    'linear-gradient(135deg, #ef4444, #dc2626)', // Red
    'linear-gradient(135deg, #ec4899, #db2777)', // Pink
    'linear-gradient(135deg, #0ea5e9, #2563eb)', // Teal
    'linear-gradient(135deg, #84cc16, #65a30d)', // Light Green
  ];
  const [selectedColor, setSelectedColor] = useState(colors[0]);

  const getSolidColor = (colorStr) => {
    if (colorStr && colorStr.includes('linear-gradient')) {
      const match = colorStr.match(/#([a-fA-F0-9]{6}|[a-fA-F0-9]{3})/);
      return match ? match[0] : '#3b82f6';
    }
    return colorStr;
  };

  // Set default region when modal opens
  useEffect(() => {
    if (isOpen) {
      setRegionId(currentRegion === 'Offshore' ? '2' : '1');
      setEmail('');
      setIsICPod(false);
      setIsBenchPod(false);
      setError('');
    }
  }, [isOpen, currentRegion]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isICPod && !email) {
      setError('Please enter a recruiter email.');
      return;
    }

    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('token');
      
      // Pass email or force IC pod name
      const podPayload = {
        email: isICPod ? undefined : email,
        teamName: isICPod ? 'Individual Contributor' : undefined,
        teamType: isBenchPod ? 'BENCH' : 'GENERAL',
        regionId: parseInt(regionId, 10),
        leadUserId: null,
        color: getSolidColor(selectedColor)
      };

      try {
        await axios.post(`${BASE_URL}/api/teams`, podPayload, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch (postErr) {
        if (postErr.response && postErr.response.status === 404) {
          await axios.post(`${BASE_URL}/api/recruitment/teams`, podPayload, {
            headers: { Authorization: `Bearer ${token}` }
          });
        } else {
          throw postErr;
        }
      }

      onPodCreated();
      onClose();
    } catch (err) {
      console.error('Error creating pod:', err);
      setError(err.response?.data?.message || 'Failed to create pod');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="pod-modal-overlay">
      <div className="pod-modal-content">
        <div className="pod-modal-header">
          <h3 className="pod-modal-title">+ Create New Pod</h3>
          <span className="pod-modal-close" onClick={onClose}><X size={20} /></span>
        </div>
        
        <div className="pod-modal-body">
          <form onSubmit={handleSubmit}>
            <div className="pod-modal-form-group" style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
              <input 
                type="checkbox" 
                id="ic-pod-checkbox"
                checked={isICPod}
                onChange={(e) => {
                  setIsICPod(e.target.checked);
                  if (e.target.checked) setEmail('');
                }}
                style={{ marginRight: '8px', cursor: 'pointer', width: '16px', height: '16px' }}
              />
              <label htmlFor="ic-pod-checkbox" className="pod-modal-label" style={{ marginBottom: 0, cursor: 'pointer' }}>
                Create as Individual Contributor Pod
              </label>
            </div>

            <div className="pod-modal-form-group" style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
              <input 
                type="checkbox" 
                id="bench-pod-checkbox"
                checked={isBenchPod}
                onChange={(e) => setIsBenchPod(e.target.checked)}
                style={{ marginRight: '8px', cursor: 'pointer', width: '16px', height: '16px' }}
              />
              <label htmlFor="bench-pod-checkbox" className="pod-modal-label" style={{ marginBottom: 0, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}>
                Bench Sales Pod <span style={{ fontSize: '11px', color: '#64748b', fontWeight: 'normal' }}>(Designate for Bench Recruiter Scorecard & Aging)</span>
              </label>
            </div>

            {!isICPod && (
              <div className="pod-modal-form-group">
                <label className="pod-modal-label">Recruiter Email <span className="required">*</span></label>
                <input 
                  type="email" 
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="e.g. name@prophecytechs.com"
                  className="pod-modal-input"
                />
              </div>
            )}

            <div className="pod-modal-form-group">
              <label className="pod-modal-label">Pod Color</label>
              <div style={{ display: 'flex', gap: '12px', marginTop: '8px' }}>
                {colors.map((color) => (
                  <div 
                    key={color}
                    onClick={() => setSelectedColor(color)}
                    style={{ 
                      width: '32px', 
                      height: '32px', 
                      borderRadius: '50%', 
                      background: color,
                      cursor: 'pointer',
                      boxShadow: selectedColor === color ? `0 0 0 2px white, 0 0 0 4px ${getSolidColor(color)}` : 'none',
                      transition: 'all 0.2s ease',
                      border: '1px solid rgba(0,0,0,0.1)'
                    }}
                  />
                ))}
              </div>
            </div>

            <div className="pod-modal-form-group">
              <label className="pod-modal-label">Pod Type</label>
              <div className="pod-modal-btn-group">
                <button
                  type="button"
                  onClick={() => setRegionId('1')}
                  className={`pod-modal-toggle-btn ${regionId === '1' ? 'active' : 'inactive'}`}
                  style={regionId === '1' ? { borderColor: '#2563eb', color: '#2563eb', background: '#eff6ff' } : {}}
                >
                  <Building2 size={16} /> Onsite
                </button>
                <button
                  type="button"
                  onClick={() => setRegionId('2')}
                  className={`pod-modal-toggle-btn ${regionId === '2' ? 'active' : 'inactive'}`}
                  style={regionId === '2' ? { borderColor: '#2563eb', color: '#2563eb', background: '#eff6ff' } : {}}
                >
                  <Globe size={16} /> Offshore
                </button>
              </div>
            </div>
            
            <div 
              style={{ 
                background: selectedColor, 
                padding: '16px', 
                borderRadius: '8px', 
                color: 'white', 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center', 
                marginTop: '24px',
                marginBottom: '16px',
                boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
              }}
            >
              <span style={{ fontWeight: '600', fontSize: '15px' }}>
                {isICPod ? `Pod Name: Individual Contributor` : email ? `Will generate name for ${email}` : 'Pod Name Preview'}
              </span>
              <span style={{ background: 'rgba(255,255,255,0.3)', padding: '4px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: 'bold' }}>
                {regionId === '1' ? 'ONSITE' : 'OFFSHORE'}
              </span>
            </div>

            {error && <div className="pod-modal-error">{error}</div>}

            <div className="pod-modal-footer">
              <button type="button" onClick={onClose} className="pod-modal-btn-cancel" style={{ borderRadius: '8px', padding: '10px 24px' }}>Cancel</button>
              <button type="submit" disabled={loading} className="pod-modal-btn-submit" style={{ borderRadius: '8px', padding: '10px 24px' }}>
                {loading ? 'Creating...' : 'Create Pod'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreatePodModal;
